import { useCallback, useEffect, useState } from 'react';
import { storageSize } from '@wr/shared';
import { get } from '../../api.js';
import { useToast } from '../../components/Toast.js';
import { Dialog, Notice } from '../../components/ui.js';
import { useOffline } from '../../offline/context.js';
import {
  IPHONE_REMINDER,
  dropAll,
  dropItem,
  isInstalledApp,
  isIos,
  keepItem,
  keptItems,
  reconcile,
  storageInfo,
  type KeptItem,
  type OfflineAsset,
} from '../../offline/media.js';
import { useOnline } from '../../offline/network.js';
import { useSignedIn } from '../../session.js';

/**
 * My account > Offline content (round 2; each person, on this device):
 * what is kept for shows without network, each item's size and state, the
 * space used and available, whether the browser promised to keep it;
 * Update (only when the person asks, roaming), Remove, Remove all.
 */
export function OfflineContent() {
  const { user, can } = useSignedIn();
  const { store } = useOffline();
  const online = useOnline();
  const toast = useToast();
  const [kept, setKept] = useState<Record<string, KeptItem> | null>(null);
  const [library, setLibrary] = useState<OfflineAsset[] | null>(null);
  const [space, setSpace] = useState<Awaited<ReturnType<typeof storageInfo>> | null>(null);
  const [busy, setBusy] = useState<{ id: string; bytes: number } | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** What this device keeps and its space; online, after the library's rules. */
  const read = useCallback(async () => {
    if (!store) return null;
    let found = await keptItems(store);
    let items: OfflineAsset[] | null = null;
    if (navigator.onLine && can('content.read')) {
      try {
        items = (await get<{ items: OfflineAsset[] }>('/content')).items;
        found = await reconcile(store, user.id, items);
      } catch {
        // Without the library, what is kept still shows.
      }
    }
    return { kept: found, library: items, space: await storageInfo() };
  }, [store, user.id, can]);
  const apply = useCallback((r: Awaited<ReturnType<typeof read>>) => {
    if (!r) return;
    setKept(r.kept);
    setSpace(r.space);
    if (r.library) setLibrary(r.library);
  }, []);
  const load = async () => apply(await read());

  useEffect(() => {
    let current = true;
    void read().then((r) => current && apply(r));
    return () => {
      current = false;
    };
  }, [read, apply]);

  async function update(item: KeptItem) {
    const a = library?.find((x) => x.id === item.id);
    if (!store || !a) return;
    setError(null);
    setBusy({ id: item.id, bytes: 0 });
    try {
      await keepItem(store, user.id, a, (bytes) => setBusy({ id: item.id, bytes }));
      toast(`${item.title} updated on this device.`);
    } catch {
      setError('Not downloaded: check the network and try again.');
    }
    setBusy(null);
    await load();
  }

  async function remove(id: string) {
    if (!store) return;
    await dropItem(store, user.id, id);
    await load();
  }

  async function removeAll() {
    setConfirm(false);
    if (!store) return;
    await dropAll(store, user.id);
    toast('Offline content removed from this device.');
    await load();
  }

  const items = Object.values(kept ?? {}).sort((a, b) => a.title.localeCompare(b.title));
  const used = items.reduce((n, i) => n + i.size, 0);
  const free = space?.usage != null && space.quota != null ? space.quota - space.usage : null;
  return (
    <section id="offline" className="section card" aria-labelledby="oc-title">
      <h2 id="oc-title" className="sec">
        Offline content
      </h2>
      <p className="note">
        Videos and PDFs kept on this device for shows without network. You choose them in Content
        (Make available offline).
      </p>
      {isIos() && <Notice kind={isInstalledApp() ? 'info' : 'warn'}>{IPHONE_REMINDER}</Notice>}
      {space && (
        <p className="note" aria-label="Space on this device">
          {storageSize(used)} kept here.
          {free !== null ? ` ${storageSize(free)} available for the portal on this device.` : ''}
        </p>
      )}
      {space?.persisted === false && items.length > 0 && (
        <Notice kind="warn">
          The browser has not promised to keep this content: it may clear it if the device runs
          short of space. Installing the portal on the home screen usually solves it.
        </Notice>
      )}
      {free !== null && free < 200 * 1024 ** 2 && (
        <Notice kind="warn">Little space left on this device: remove some content first.</Notice>
      )}
      {kept && items.length === 0 && <p className="note">Nothing kept on this device yet.</p>}
      {items.length > 0 && (
        <div className="offline-list">
          {items.map((i) => (
            <div key={i.id} className="purge-row">
              <span>
                <b>{i.title}</b>
                <span className="note">
                  {i.format === 'video' ? 'Video' : 'PDF'}, {storageSize(i.size)}
                  {i.update
                    ? `. Update available: ${storageSize(i.update.size)}`
                    : ', ready offline'}
                </span>
              </span>
              <span className="offline-acts">
                {i.update && (
                  <button
                    type="button"
                    className="btn sm primary"
                    disabled={!online || busy !== null}
                    aria-label={`Update ${i.title}`}
                    onClick={() => void update(i)}
                  >
                    {busy?.id === i.id
                      ? `${Math.round((busy.bytes / Math.max(1, i.update.size)) * 100)}%`
                      : 'Update'}
                  </button>
                )}
                <button
                  type="button"
                  className="btn sm"
                  aria-label={`Remove ${i.title}`}
                  disabled={busy?.id === i.id}
                  onClick={() => void remove(i.id)}
                >
                  Remove
                </button>
              </span>
            </div>
          ))}
        </div>
      )}
      {error && <Notice kind="error">{error}</Notice>}
      {items.length > 0 && (
        <div className="actions">
          <button type="button" className="btn danger" onClick={() => setConfirm(true)}>
            Remove all
          </button>
        </div>
      )}
      {confirm && (
        <Dialog
          small
          title="Remove all offline content?"
          onClose={() => setConfirm(false)}
          footer={
            <>
              <button type="button" className="btn" autoFocus onClick={() => setConfirm(false)}>
                Cancel
              </button>
              <button type="button" className="btn primary" onClick={() => void removeAll()}>
                Remove all
              </button>
            </>
          }
        >
          <p className="dlg-text">
            {items.length} item{items.length === 1 ? '' : 's'} ({storageSize(used)}) leave this
            device. Without network they will no longer play.
          </p>
        </Dialog>
      )}
    </section>
  );
}
